import { useState, useRef, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Image,
  Flex,
  Text,
  SimpleGrid,
  Badge,
  Divider,
  HStack,
  VStack
} from "@chakra-ui/react";
import { ChevronUpIcon, ChevronDownIcon, RepeatIcon } from "@chakra-ui/icons";
import { formatDate, formatStatus, getStatusColor, getInfractionClassLabel } from "../../utils/formatters";

const ImageModal = ({ 
  isOpen, 
  onClose, 
  selectedImage, 
  selectedInfraction, 
  updateInfractionStatus,
  getAlgorithmName,
  getCameraName,
  // Novos props para navegação
  infractions = [],
  currentIndex,
  setCurrentIndex,
  loadMoreInfractions,    // async () => Promise<void>
  hasMore,                // boolean se ainda há mais para carregar
  isLoadingMore,          // opcional: estado de carregamento vindo do pai
  userLevel = 1           // Adicionar userLevel como prop
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [loadingNext, setLoadingNext] = useState(false);
  const [pendingIndexUpdate, setPendingIndexUpdate] = useState(null);
  const [showBbox, setShowBbox] = useState(false);
  const [imageNaturalWidth, setImageNaturalWidth] = useState(1);
  const [imageNaturalHeight, setImageNaturalHeight] = useState(1);
  const imageRef = useRef(null);

  // CORREÇÃO: Obter a infração atual baseada no currentIndex
  const currentInfraction = infractions[currentIndex] || selectedInfraction;
  const currentImage = currentInfraction?.imageUrl || selectedImage;

  const zoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 4));
  const zoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 1));
  const resetZoom = () => { setZoomLevel(1); setPosition({ x: 0, y: 0 }); };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };
  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };
  const handleMouseUp = () => setIsDragging(false);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex, setCurrentIndex]);

  // Effect para detectar quando novas infrações foram carregadas
  useEffect(() => {
    if (pendingIndexUpdate !== null && infractions.length > pendingIndexUpdate) {
      setCurrentIndex(pendingIndexUpdate);
      setPendingIndexUpdate(null);
    }
  }, [infractions.length, pendingIndexUpdate, setCurrentIndex]);

  const handleNext = useCallback(async () => {
    if (!infractions.length) return;
    const lastIndex = infractions.length - 1;

    // Se ainda há próximo dentro do array atual
    if (currentIndex < lastIndex) {
      setCurrentIndex(currentIndex + 1);
      return;
    }

    // Se está no último e há mais para buscar
    if (currentIndex === lastIndex && hasMore && loadMoreInfractions && !loadingNext && !isLoadingMore) {
      try {
        setLoadingNext(true);
        const nextIndex = currentIndex + 1;
        setPendingIndexUpdate(nextIndex); // Marcar que queremos ir para este índice
        await loadMoreInfractions(); // Carregar mais infrações
      } catch (error) {
        console.error('Erro ao carregar mais infrações:', error);
        setPendingIndexUpdate(null); // Cancelar atualização pendente
      } finally {
        setLoadingNext(false);
      }
    }
  }, [currentIndex, infractions.length, hasMore, loadMoreInfractions, setCurrentIndex, loadingNext, isLoadingMore]);

  // Função para encontrar a próxima infração pendente
  const findNextPendingInfraction = useCallback(() => {
    // Procurar a partir do índice atual + 1
    for (let i = currentIndex + 1; i < infractions.length; i++) {
      if (infractions[i].status === 'pending') {
        return i;
      }
    }
    // Se não encontrou após o índice atual, retornar -1 para carregar mais
    return -1;
  }, [currentIndex, infractions]);

  // Função para lidar com atualização de status e navegar para próxima pendente
  const handleStatusUpdate = useCallback(async (id, status) => {
    try {
      await updateInfractionStatus(id, status);
      
      // Encontrar próxima infração pendente
      const nextPendingIndex = findNextPendingInfraction();
      
      if (nextPendingIndex !== -1) {
        // Se encontrou uma próxima pendente, navegar para ela
        setCurrentIndex(nextPendingIndex);
        resetZoom(); // Reset zoom ao mudar de infração
      } else {
        // Se não encontrou, tentar carregar mais infrações se possível
        if (hasMore && loadMoreInfractions && !loadingNext && !isLoadingMore) {
          try {
            setLoadingNext(true);
            const currentLength = infractions.length;
            await loadMoreInfractions(); // Carregar mais infrações
            
            // Aguardar um tempo para as infrações serem carregadas
            setTimeout(() => {
              // Procurar novamente nas infrações carregadas
              let foundPendingIndex = -1;
              for (let i = currentLength; i < infractions.length; i++) {
                if (infractions[i] && infractions[i].status === 'pending') {
                  foundPendingIndex = i;
                  break;
                }
              }
              
              if (foundPendingIndex !== -1) {
                setCurrentIndex(foundPendingIndex);
                resetZoom();
              } else {
                // Se ainda não encontrou pendentes, fechar o modal
                onClose();
              }
            }, 100);
          } catch (error) {
            console.error('Erro ao carregar mais infrações:', error);
            onClose();
          } finally {
            setLoadingNext(false);
          }
        } else {
          // Se não há mais para carregar, fechar o modal
          onClose();
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      // Em caso de erro, não navegar
    }
  }, [updateInfractionStatus, findNextPendingInfraction, setCurrentIndex, onClose, hasMore, loadMoreInfractions, loadingNext, isLoadingMore, infractions.length]);

  const handleKeyDown = (e) => {
    if (!isOpen || !currentInfraction) return;
    const key = e.key.toLowerCase();
    switch (key) {
      case 'c':
        // Só permitir confirmar se for nível 4
        if (userLevel >= 4) {
          handleStatusUpdate(currentInfraction.id, 'confirmed');
        }
        break;
      case 'r':
        handleStatusUpdate(currentInfraction.id, 'rejected');
        break;
      case 'p':
        handleStatusUpdate(currentInfraction.id, 'pending');
        break;
      case 'arrowright':
        handleNext();
        break;
      case 'arrowleft':
        handlePrev();
        break;
      case 'x':
      case 'escape':
        onClose();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentInfraction, handleNext, handlePrev, handleStatusUpdate, userLevel]);

  useEffect(() => {
    if (!isOpen) resetZoom();
    setShowBbox(false);
    setImageNaturalWidth(1);
    setImageNaturalHeight(1);
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent
        maxW="calc(100vw - 32px)"
        h="calc(100vh - 32px)"
        m="16px"
        borderRadius="2xl"
        overflow="hidden"
        display="flex"
        boxShadow="2xl"
        bg="white"
      >
        <ModalHeader>
          Detalhe da Infração
          {typeof currentIndex === 'number' && infractions.length > 0 && (
            <Text as="span" ml={3} fontSize="sm" color="gray.500">
              {currentIndex + 1} / {infractions.length}{hasMore ? '+' : ''}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={0} flex="1" display="flex" overflow="hidden">
          <Flex w="100%" h="100%" overflow="hidden">
            {/* Área da imagem */}
            <Box flex="1" position="relative" bg="gray.900" display="flex" alignItems="center" justifyContent="center">
              {/* Botão Prev (overlay) */}
              <Button
                position="absolute"
                left="8px"
                top="50%"
                transform="translateY(-50%)"
                size="sm"
                variant="solid"
                colorScheme="blackAlpha"
                onClick={handlePrev}
                isDisabled={currentIndex <= 0}
                opacity={currentIndex <= 0 ? 0.3 : 0.8}
                _hover={{ opacity: 1 }}
              >
                ←
              </Button>
              {/* Botão Next (overlay) */}
              <Button
                position="absolute"
                right="8px"
                top="50%"
                transform="translateY(-50%)"
                size="sm"
                variant="solid"
                colorScheme="blackAlpha"
                onClick={handleNext}
                isDisabled={
                  (!hasMore && currentIndex === infractions.length - 1) ||
                  loadingNext || isLoadingMore
                }
                opacity={(
                  (!hasMore && currentIndex === infractions.length - 1)
                ) ? 0.3 : 0.8}
                _hover={{ opacity: 1 }}
              >
                {loadingNext || isLoadingMore ? '...' : '→'}
              </Button>

              <Box
                overflow="hidden"
                borderRadius="md"
                position="relative"
                w="100%"
                h="100%"
                cursor={zoomLevel > 1 ? "grab" : "default"}
                onMouseDown={handleMouseDown}
                style={{ touchAction: "none" }}
              >
                <Box
                  ref={imageRef}
                  position="absolute"
                  width="100%"
                  height="100%"
                  style={{
                    transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                    transformOrigin: "center",
                    transition: isDragging ? "none" : "transform 0.2s ease-out"
                  }}
                >
                  <Image
                    src={currentImage}
                    alt="Imagem da infração"
                    objectFit="contain"
                    w="100%"
                    h="100%"
                    draggable="false"
                    onLoad={(e) => {
                      setImageNaturalWidth(e.target.naturalWidth || 1);
                      setImageNaturalHeight(e.target.naturalHeight || 1);
                    }}
                  />
                  {/* Overlay bbox */}
                  {showBbox && currentInfraction?.bbox && (() => {
                    const parts = currentInfraction.bbox.split(',').map(Number);
                    if (parts.length !== 4 || parts.some(isNaN)) return null;
                    const [x1, y1, x2, y2] = parts;
                    return (
                      <Box
                        position="absolute"
                        border="3px solid red"
                        borderRadius="2px"
                        pointerEvents="none"
                        left={`${(x1 / imageNaturalWidth) * 100}%`}
                        top={`${(y1 / imageNaturalHeight) * 100}%`}
                        width={`${((x2 - x1) / imageNaturalWidth) * 100}%`}
                        height={`${((y2 - y1) / imageNaturalHeight) * 100}%`}
                      />
                    );
                  })()}
                </Box>
              </Box>

              {/* Controles de zoom */}
              <Flex
                position="absolute"
                bottom="16px"
                right="16px"
                bg="blackAlpha.700"
                borderRadius="md"
                p={1}
                direction="column"
                align="center"
                color="white"
              >
                <Button size="sm" variant="ghost" onClick={zoomIn} color="white" p={1}>
                  <ChevronUpIcon boxSize={5} />
                </Button>
                <Text fontWeight="bold" fontSize="sm" my={1}>
                  {Math.round(zoomLevel * 100)}%
                </Text>
                <Button size="sm" variant="ghost" onClick={zoomOut} color="white" p={1}>
                  <ChevronDownIcon boxSize={5} />
                </Button>
                <Divider my={1} />
                <Button size="sm" variant="ghost" onClick={resetZoom} color="white" p={1}>
                  <RepeatIcon boxSize={4} />
                </Button>
              </Flex>

              {/* Botão toggle bbox */}
              <Button
                position="absolute"
                bottom="16px"
                left="50%"
                transform="translateX(-50%)"
                size="sm"
                colorScheme={showBbox ? 'red' : 'blue'}
                onClick={() => setShowBbox(v => !v)}
                isDisabled={!currentInfraction?.bbox}
                opacity={currentInfraction?.bbox ? 0.9 : 0.3}
              >
                {showBbox ? 'Ocultar Detecção' : 'Visualizar Infração'}
              </Button>
            </Box>

            {/* Painel lateral */}
            <Box
              w={["100%", "380px"]}
              maxW="380px"
              borderLeft="1px solid"
              borderColor="gray.200"
              bg="white"
              p={4}
              overflowY="auto"
            >
              {currentInfraction && (
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontWeight="bold" mb={2}>Detalhes</Text>
                    <SimpleGrid columns={1} spacing={2}>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">ID</Text>
                        <Text fontSize="sm">{currentInfraction.id}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">Status</Text>
                        <Badge colorScheme={getStatusColor(currentInfraction.status)}>
                          {formatStatus(currentInfraction.status)}
                        </Badge>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">Detecção</Text>
                        <Text fontSize="sm">
                          {getInfractionClassLabel(currentInfraction) || getAlgorithmName(currentInfraction.algorithm_id)}
                        </Text>
                        <Text fontSize="xs" color="gray.400" mt={0.5}>{getAlgorithmName(currentInfraction.algorithm_id)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">Câmera</Text>
                        <Text fontSize="sm">{getCameraName(currentInfraction.camera_id)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">Confiança</Text>
                        <Text fontSize="sm">{(currentInfraction.confidence * 100).toFixed(1)}%</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">Veículo</Text>
                        <Text fontSize="sm">{currentInfraction.vehicle_type}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">Data/Hora</Text>
                        <Text fontSize="sm">{formatDate(currentInfraction.detected_at)}</Text>
                      </Box>
                      {currentInfraction.reviewed_by && (
                        <Box>
                          <Text fontSize="sm" fontWeight="bold">Analisada por</Text>
                          <Text fontSize="sm">
                            {currentInfraction.reviewer
                              ? (currentInfraction.reviewer.name || currentInfraction.reviewer.username || currentInfraction.reviewer.id)
                              : currentInfraction.reviewed_by}
                          </Text>
                        </Box>
                      )}
                      {currentInfraction.plate && (
                        <Box>
                          <Text fontSize="sm" fontWeight="bold">Placa</Text>
                          <Text fontSize="sm">{currentInfraction.plate}</Text>
                        </Box>
                      )}
                      {currentInfraction.registeredVehicleType && (
                        <Box>
                          <Text fontSize="sm" fontWeight="bold">Placa cadastrada</Text>
                          <Badge colorScheme="teal">{currentInfraction.registeredVehicleType}</Badge>
                        </Box>
                      )}
                    </SimpleGrid>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Atualizar Status
                      <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                        ({userLevel >= 4 ? 'C Confirmar • ' : ''}R Rejeitar • P Pendente • ← / → Navegar • X Fechar)
                      </Text>
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      <Button
                        colorScheme="green"
                        size="sm"
                        onClick={() => handleStatusUpdate(currentInfraction.id, 'confirmed')}
                        isDisabled={userLevel < 4}
                        opacity={userLevel < 4 ? 0.4 : 1}
                        cursor={userLevel < 4 ? 'not-allowed' : 'pointer'}
                        title={userLevel < 4 ? 'Apenas usuários nível 4 podem confirmar infrações' : ''}
                      >
                        Confirmar {userLevel >= 4 ? '(C)' : '(Nível 4 necessário)'}
                      </Button>
                      <Button
                        colorScheme="yellow"
                        size="sm"
                        onClick={() => handleStatusUpdate(currentInfraction.id, 'pending')}
                      >
                        Pendente (P)
                      </Button>
                      <Button
                        colorScheme="red"
                        size="sm"
                        onClick={() => handleStatusUpdate(currentInfraction.id, 'rejected')}
                      >
                        Rejeitar (R)
                      </Button>
                      <HStack pt={1}>
                        <Button
                          flex="1"
                          variant="outline"
                          size="sm"
                          onClick={handlePrev}
                          isDisabled={currentIndex <= 0}
                        >
                          ← Anterior
                        </Button>
                        <Button
                          flex="1"
                          variant="outline"
                          size="sm"
                          onClick={handleNext}
                          isDisabled={
                            (!hasMore && currentIndex === infractions.length - 1) ||
                            loadingNext || isLoadingMore
                          }
                        >
                          {loadingNext || isLoadingMore
                            ? 'Carregando...'
                            : 'Próxima →'}
                        </Button>
                      </HStack>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                      >
                        Fechar (X)
                      </Button>
                    </VStack>
                  </Box>
                </VStack>
              )}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter display="none" />
      </ModalContent>
    </Modal>
  );
};

export default ImageModal;